import React, { useCallback } from 'react';
import { Checkbox } from '@pwc/appkit-react/lib/Checkbox';
import Select from '../../../shared/reactSelect/select.component';
import { SftpSchedule, RecurrencePattern } from '@common/types/sftpSchedule';
import { formatHour } from './formatHour';

const RECURRENCE_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const DAY_OF_WEEK_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: formatHour(i),
}));

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
  value: i + 1,
  label: `${i + 1}`,
}));

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Berlin', label: 'Central European (CET)' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
];

type ScheduleRecurrenceEditorProps = {
  schedule: SftpSchedule;
  onChange: (schedule: SftpSchedule) => void;
};

export const ScheduleRecurrenceEditor = ({
  schedule,
  onChange,
}: ScheduleRecurrenceEditorProps) => {
  const handleEnabledToggle = useCallback(() => {
    onChange({ ...schedule, enabled: !schedule.enabled });
  }, [schedule, onChange]);

  const handlePatternChange = useCallback((option: { value: RecurrencePattern }) => {
    const newRecurrence = { pattern: option.value } as SftpSchedule['recurrence'];
    if (option.value === 'weekly') {
      newRecurrence.daysOfWeek = schedule.recurrence.daysOfWeek ?? [1];
    }
    if (option.value === 'monthly') {
      newRecurrence.dayOfMonth = schedule.recurrence.dayOfMonth ?? 1;
    }
    onChange({ ...schedule, recurrence: newRecurrence });
  }, [schedule, onChange]);

  const handleDaysOfWeekChange = useCallback((options: { value: number }[]) => {
    const days = options ? options.map(o => o.value) : [];
    onChange({
      ...schedule,
      recurrence: { ...schedule.recurrence, daysOfWeek: days },
    });
  }, [schedule, onChange]);

  const handleDayOfMonthChange = useCallback((option: { value: number }) => {
    onChange({
      ...schedule,
      recurrence: { ...schedule.recurrence, dayOfMonth: option.value },
    });
  }, [schedule, onChange]);

  const handleHourChange = useCallback((option: { value: number }) => {
    onChange({ ...schedule, runHourLocal: option.value });
  }, [schedule, onChange]);

  const handleTimezoneChange = useCallback((option: { value: string }) => {
    onChange({ ...schedule, timezone: option.value });
  }, [schedule, onChange]);

  return (
    <section className="batch-schedule-section" aria-labelledby="batch-schedule-heading">
      <h3 id="batch-schedule-heading" className="batch-schedule-section__title">
        SFTP delivery schedule
      </h3>
      <p className="batch-schedule-section__description">
        When enabled, TLS runs this batch automatically at the selected local time and sends output via SFTP.
      </p>

      <div className="batch-schedule-section__toggle">
        <Checkbox
          onChange={handleEnabledToggle}
          checked={schedule.enabled}
        >
          Enable scheduled SFTP delivery
        </Checkbox>
      </div>

      {schedule.enabled && (
        <div className="batch-schedule-section__fields">
          <div className="batch-schedule-section__row">
            <div className="batch-schedule-section__field">
              <span className="batch-schedule-section__label">Frequency</span>
              <Select
                className="select-custom-reports batch-schedule-section__select"
                clearable={false}
                options={RECURRENCE_OPTIONS}
                value={RECURRENCE_OPTIONS.find(o => o.value === schedule.recurrence.pattern)}
                onChange={handlePatternChange}
                closeOnSelect
              />
            </div>

            <div className="batch-schedule-section__field">
              <span className="batch-schedule-section__label">Run time</span>
              <Select
                className="select-custom-reports batch-schedule-section__select"
                clearable={false}
                options={HOUR_OPTIONS}
                value={HOUR_OPTIONS.find(o => o.value === schedule.runHourLocal)}
                onChange={handleHourChange}
                closeOnSelect
              />
            </div>

            <div className="batch-schedule-section__field">
              <span className="batch-schedule-section__label">Timezone</span>
              <Select
                className="select-custom-reports batch-schedule-section__select"
                clearable={false}
                options={TIMEZONE_OPTIONS}
                value={TIMEZONE_OPTIONS.find(o => o.value === schedule.timezone)}
                onChange={handleTimezoneChange}
                closeOnSelect
              />
            </div>
          </div>

          {schedule.recurrence.pattern === 'weekly' && (
            <div className="batch-schedule-section__row">
              <div className="batch-schedule-section__field batch-schedule-section__field--wide">
                <span className="batch-schedule-section__label">Days of week</span>
                <Select
                  className="select-custom-reports batch-reports-multiselect batch-schedule-section__select"
                  clearable={false}
                  multi
                  options={DAY_OF_WEEK_OPTIONS}
                  value={DAY_OF_WEEK_OPTIONS.filter(
                    o => schedule.recurrence.daysOfWeek?.includes(o.value)
                  )}
                  onChange={handleDaysOfWeekChange}
                />
              </div>
            </div>
          )}

          {schedule.recurrence.pattern === 'monthly' && (
            <div className="batch-schedule-section__row">
              <div className="batch-schedule-section__field">
                <span className="batch-schedule-section__label">Day of month</span>
                <Select
                  className="select-custom-reports batch-schedule-section__select"
                  clearable={false}
                  options={DAY_OF_MONTH_OPTIONS}
                  value={DAY_OF_MONTH_OPTIONS.find(o => o.value === schedule.recurrence.dayOfMonth)}
                  onChange={handleDayOfMonthChange}
                  closeOnSelect
                />
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
